import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import { ConfirmModal } from "../../../../../controls/modals/ConfirmModal";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import {
  CATALOG_SAMPLE_TYPE_INITIAL_VALUES,
  CatalogSampleType,
} from "../models/CatalogSampleType";
import { validateNoSpecialChars } from "../../../validates";
import { useUpsertCatalogSampleTypeMutation } from "../hooks/mutations/useUpsertCatalogSampleTypeMutation";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  sampleType: CatalogSampleType | null;
  sampleTypes: CatalogSampleType[] | null;
  showModal: "new" | "edit" | "delete" | "";
  onHide: ({ entityId, shouldUpdate }: onModalHideProps) => void;
};
export const SampleTypeModal = ({
  sampleType,
  sampleTypes,
  showModal,
  onHide,
}: Props) => {
  const upsertMutation = useUpsertCatalogSampleTypeMutation();

  const [notificationMessage, setNotificationMessage] = useState("");
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [existingSampleType, setExistingSampleType] = useState("");
  const [title, setTitle] = useState("");

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<CatalogSampleType>({
    defaultValues: CATALOG_SAMPLE_TYPE_INITIAL_VALUES,
  });

  const isFormDirty =
    !(Object.keys(dirtyFields).length === 0) || showModal == "new";

  const onSubmit: SubmitHandler<CatalogSampleType> = async (
    data: CatalogSampleType,
  ) => {
    const allSampleTypes: CatalogSampleType[] = sampleTypes || [];
    if (showModal !== "delete") {
      if (
        allSampleTypes.some(
          (st) =>
            st.SampleTypeName === data.SampleTypeName &&
            st.SampleTypeId !== data.SampleTypeId,
        )
      ) {
        setExistingSampleType(data.SampleTypeName);
        setConfirmOpen(true);
      } else {
        await performUpsert(data);
      }
    }
  };

  const performUpsert = async (data: CatalogSampleType) => {
    try {
      const resp = await upsertMutation.mutateAsync(data);
      const entityId = get(resp, "data.tables[0].rows[0].Id", 0);
      setNotificationMessage("The sample type was saved successfully");
      onHide({ entityId: entityId, shouldUpdate: true });
    } catch (err: any) {
      setError(err.message);
      onHide({ shouldUpdate: false });
    }
  };

  useEffect(() => {
    if (showModal != "") {
      setTitle(showModal.toUpperCase());
      reset(sampleType || CATALOG_SAMPLE_TYPE_INITIAL_VALUES);
    }
  }, [showModal]);

  return (
    <>
      <ConfirmModal
        show={confirmOpen}
        onHide={(proceed: boolean) => {
          if (proceed) {
            performUpsert(getValues());
            onHide({ shouldUpdate: true });
          }
          setConfirmOpen(false);
        }}
        existingName={existingSampleType}
        label="Sample Type"
      />
      <MesfModal
        title={`${title} SAMPLE TYPE`}
        open={showModal != ""}
        handleClose={() => onHide({ shouldUpdate: false })}
        id="sample-type-modal"
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers style={{ padding: "15px 30px" }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={12}>
                <Controller
                  name="SampleTypeName"
                  control={control}
                  rules={{
                    required: "Name is required",
                    validate: validateNoSpecialChars,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      label="Name"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="IsActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      style={{
                        margin: 0,
                        height: "100%",
                      }}
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="Enabled"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={upsertMutation.isLoading}
              onHide={() => onHide({ shouldUpdate: false })}
              buttonLabel={showModal === "delete" ? "Delete" : "Save"}
              buttonColor={showModal === "delete" ? "secondary" : "primary"}
              disabled={!isFormDirty && showModal !== "delete"}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error with SampleType"
      />
    </>
  );
};
